import { Router } from "@frui.ts/screens";
import type { interfaces } from "inversify";
import { computed } from "mobx";
import { Modules } from "config";
import ChargePrice, { ChargePriceValidationRules } from "entities/chargePrice";
import type ChargeType from "entities/chargeType";
import { ChargeTypeValidationRules } from "entities/chargeType";
import ChargeValidity from "entities/chargeValidity";
import EnumService from "services/enumService";
import PriceListService from "services/priceListService";
import { buildPriceListChargeTypes } from "utils/priceListChargeTypes";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import { attachAutomaticValidator, attachValidatorAggregator } from "utils/validatorAggregator";
import OptionsService from "services/optionsService";
import SecurityService from "services/securityService";
import DetailViewModel from "viewModels/detailViewModel";

@Router.registerRoute({ name: Router.Self, route: ":chargeTypeId" })
export default class ChargeTypeDetailViewModel extends DetailViewModel<ChargeType> {
  readonly modelName = "chargeType";

  constructor(
    protected itemId: string,
    protected localizationService: LocalizationService,
    protected notificationService: NotificationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    private priceListService: PriceListService,
    private enumService: EnumService,
    private optionsService: OptionsService
  ) {
    super(itemId);
  }

  protected async loadDetail() {
    await this.priceListService.startChargeTypePriceListsInitialization();

    const item = buildPriceListChargeTypes(this.priceListService, this.enumService).find(x => x.id === this.itemId);
    if (!item) {
      throw new Error(`Charge type ${this.itemId} was not found in any price list.`);
    }

    this.validFuelTypes.forEach(fuelType => {
      if (!~item.prices.findIndex(price => price.fuelType === fuelType.fuelType)) {
        item.prices.push(new ChargePrice(fuelType.fuelType));
      }
    });
    attachAutomaticValidator(item, ChargeTypeValidationRules);
    attachValidatorAggregator(
      item,
      item.prices.map(price => attachAutomaticValidator(price, ChargePriceValidationRules))
    );

    return item;
  }

  @computed
  get validFuelTypes() {
    return this.enumService
      .values("fuel_types")
      .filter(fuelType => fuelType.localizations.find(l => l.languageCode === "CS") && fuelType.isDeleted === false);
  }

  @computed
  get allFuelTypes() {
    return this.enumService.values("fuel_types").filter(fuelType => fuelType.name);
  }

  @computed
  get validForOptions() {
    return this.optionsService.internalEnumOptions(ChargeValidity);
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.timeCharges, `chargeTypes.${code}`, size);
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string) =>
      new ChargeTypeDetailViewModel(
        itemId,
        container.get(LocalizationService),
        container.get(NotificationService),
        container.get(EventBus),
        container.get(SecurityService),
        container.get(PriceListService),
        container.get(EnumService),
        container.get(OptionsService)
      );
  }
}
